import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Filter, Map, Activity, ExternalLink, Zap, AlertTriangle,
    X, MapPin, Mountain
} from "lucide-react";
import { api } from "@/lib/api";
import { absolutizeUrl } from "@/components/ImagePicker";
import { useLang } from "@/context/LanguageContext";
import WeatherWidget from "@/components/WeatherWidget";
import WetTrailsAlert from "@/components/WetTrailsAlert";
import ConditionsMeta from "@/components/ConditionsMeta";
import { THEMES, trailStatus, fmtDateTime } from "@/components/trailCondition";
import ShareAllTrailsStatusButton from "@/components/ShareAllTrailsStatusButton";
import SEO from "@/components/SEO";
import PageHeader, { SectionMark } from "@/components/PageHeader";
import { MataTag } from "@/components/MataMark";
import MapFragment, { ElevationProfile } from "@/components/MapFragment";
import DecorationLayer from "@/components/DecorationLayer";
import { StageBadge } from "@/components/NextRace";
import { ARGENTARIO_COORDS } from "@/components/EditorialLabel";

export const diffColor = (d) => ({
    facile: "text-abnormal-acid border-abnormal-acid/50",
    medio: "text-abnormal-warn border-abnormal-warn/50",
    hard: "text-abnormal-red border-abnormal-red/50",
    blue: "text-sky-400 border-sky-400/60",
    red: "text-abnormal-red border-abnormal-red/60",
    black: "text-abnormal-paper border-white/70",
}[d] || "text-abnormal-paper border-white/20");

export const DIFF_DOT = { blue: "bg-sky-400", red: "bg-abnormal-red", black: "bg-abnormal-paper" };

/* ---------------------------------------------------------------- */
/*  STATUS BADGE — Aperto / Attenzione / Chiuso                     */
/* ---------------------------------------------------------------- */
const STATE_THEME = THEMES;

function StateBadge({ state, label }) {
    const theme = STATE_THEME[state] || STATE_THEME.unknown;
    return (
        <span data-testid={`trail-state-${state}`}
            className={`inline-flex items-center gap-1.5 px-2 py-1 border ${theme.bg} ${theme.border} ${theme.text} text-[10px] uppercase tracking-[0.25em] font-bold`}>
            <span className={`w-1.5 h-1.5 rounded-full ${theme.dot} animate-pulse`} />
            {label}
        </span>
    );
}

/* ---------------------------------------------------------------- */
/*  Time-ago helper                                                 */
/* ---------------------------------------------------------------- */
const tr_ = (t, key, fallback) => { const v = t(key); return !v || v === key || v.startsWith("trailMeta.") ? fallback : v; };


/* ---------------------------------------------------------------- */
/*  PAGE                                                            */
/* ---------------------------------------------------------------- */
export default function Trails() {
    const { t, lang } = useLang();
    const [trails, setTrails] = useState([]);
    const [combos, setCombos] = useState([]);
    const [difficulty, setDifficulty] = useState("all");
    const [terrain, setTerrain] = useState("all");
    const [state, setState] = useState("all");
    const [showCombos, setShowCombos] = useState(false);

    useEffect(() => {
        api.get("/trails").then((r) => setTrails(r.data)).catch(() => setTrails([]));
        api.get("/combos").then((r) => setCombos(r.data)).catch(() => setCombos([]));
    }, []);

    const DIFFICULTIES = useMemo(() => [
        { value: "all", label: t("trails.all") },
        { value: "blue", label: "Blue" },
        { value: "red", label: "Red" },
        { value: "black", label: "Black" },
    ], [t]);
    const TERRAINS = useMemo(() => [
        { value: "all", label: t("trails.all") },
        { value: "roccia", label: t("trails.rock") },
        { value: "flow", label: t("trails.flow") },
        { value: "tecnico", label: t("trails.technical") },
        { value: "misto", label: t("trails.mixed") },
    ], [t]);
    const STATES = useMemo(() => [
        { value: "all",     label: t("trails.all") },
        { value: "open",    label: t("trails.stateOpen") },
        { value: "warning", label: t("trails.stateWarning") },
        { value: "closed",  label: t("trails.stateClosed") },
    ], [t]);

    const filtered = useMemo(() => trails.filter((tr) =>
        (difficulty === "all" || tr.difficulty === difficulty) &&
        (terrain === "all" || tr.terrain === terrain) &&
        (state === "all" || (tr.state || "open") === state)
    ), [trails, difficulty, terrain, state]);

    // Quick stats
    const stats = useMemo(() => {
        const eff = (tr) => trailStatus(tr, lang).key;
        const open = trails.filter((tr) => eff(tr) === "dry_verified").length;
        const warn = trails.filter((tr) => ["warning", "wet_auto", "wet_verified"].includes(eff(tr))).length;
        const closed = trails.filter((tr) => eff(tr) === "closed").length;
        const unverified = trails.filter((tr) => eff(tr) === "unknown").length;
        return { open, warn, closed, unverified, total: trails.length };
    }, [trails, lang]);

    const resetFilters = () => { setDifficulty("all"); setTerrain("all"); setState("all"); };
    const hasFilters = difficulty !== "all" || terrain !== "all" || state !== "all";

    return (
        <div data-testid="trails-page" className="bg-abnormal-ink relative">
            <DecorationLayer page="trails" />
            <SEO
                title={lang === "en" ? "Argentario MTB Trail Report" : "Trail Report Argentario"}
                description={lang === "en"
                    ? `Live trail report: ${trails.length || 14} MTB Enduro trails on Monte Argentario with status (open/warning/closed), difficulty, length, elevation, Komoot and Google Maps links.`
                    : `Trail report live: ${trails.length || 14} trail MTB Enduro a Monte Argentario con stato (aperto/attenzione/chiuso), difficoltà, lunghezza, dislivello, link Komoot e Google Maps.`}
                path="/trail"
            />

            {/* ---- HEADER ---- */}
            <PageHeader
                label="TRAIL REPORT · LIVE"
                title={t("trails.title1")}
                accent={t("trails.title2")}
                tagline={[`${stats.total} trail`, lang === "en" ? "Live status" : "Stato live", "Monte Argentario"]}
                sub={t("trails.subtitle")}
                right={<div className="flex flex-wrap items-center gap-3 md:gap-6 max-w-full"><MataTag lang={lang} size={48} testid="trails-mata" /><ShareAllTrailsStatusButton trails={trails} /></div>}
            />

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-14 md:pb-20">
            {/* ---- LIVE STATUS BAR (the operational heart) ---- */}
            <div data-testid="trails-status-overview"
                className="mt-10 grid grid-cols-2 md:grid-cols-4 border border-white/15 bg-abnormal-charcoal">
                {[
                    { k: "total",  label: t("trails.statsTotal"),   value: stats.total,  color: "text-abnormal-paper" },
                    { k: "open",   label: t("trails.statsOpen"),    value: stats.open,   color: "text-abnormal-acid", sub: stats.unverified ? `${stats.unverified} ${t("trails.stateUnverified").toLowerCase()}` : null },
                    { k: "warn",   label: t("trails.statsWarning"), value: stats.warn,   color: "text-abnormal-warn" },
                    { k: "closed", label: t("trails.statsClosed"),  value: stats.closed, color: "text-abnormal-red" },
                ].map((s, i) => (
                    <div key={s.k} data-testid={`stat-${s.k}`}
                        className={`p-5 ${i > 0 ? "border-t md:border-t-0 md:border-l border-white/15" : ""} ${i === 1 ? "border-l md:border-l" : ""}`}>
                        <p className="label-mono text-abnormal-paperDim/60">{s.label}</p>
                        <p className={`font-display text-5xl uppercase mt-1 leading-none ${s.color}`}>{s.value}</p>
                        {s.sub && <p className="label-mono text-abnormal-paperDim/60 mt-2 !text-[9px]" data-testid="stat-unverified">+ {s.sub}</p>}
                    </div>
                ))}
            </div>

            {/* Live weather */}
            <WetTrailsAlert className="mt-10" />
            <ConditionsMeta className="mt-4" />
            <WeatherWidget />

            {/* Maps external links */}
            <div data-testid="trail-maps-row" className="mt-10 grid md:grid-cols-2 gap-4">
                <a href="https://www.trailforks.com/region/monte-argentario/" target="_blank" rel="noreferrer" data-testid="link-trailforks-region"
                    className="group border border-white/15 bg-abnormal-charcoal hover:border-abnormal-acid transition-colors p-5 flex items-center gap-4">
                    <div className="w-12 h-12 border border-white/20 flex items-center justify-center text-abnormal-paper group-hover:border-abnormal-acid group-hover:text-abnormal-acid transition-colors">
                        <Map size={22} />
                    </div>
                    <div className="flex-1">
                        <p className="label-mono text-abnormal-paperDim/60">{t("trails.mapTracks")}</p>
                        <p className="font-display text-2xl uppercase leading-none mt-1 text-abnormal-paper">{t("trails.trailforks")}</p>
                    </div>
                    <ExternalLink className="text-abnormal-paperDim/60 group-hover:text-abnormal-acid" size={18} />
                </a>
                <a href="https://www.strava.com/maps/global-heatmap?sport=All&style=standard&terrain=false&labels=true&poi=true&cPhotos=true&3d=false&gColor=mobileblue&gOpacity=100#10.78/42.4097/11.1512" target="_blank" rel="noreferrer" data-testid="link-strava-heatmap"
                    className="group border border-white/15 bg-abnormal-charcoal hover:border-abnormal-acid transition-colors p-5 flex items-center gap-4">
                    <div className="w-12 h-12 border border-white/20 flex items-center justify-center text-abnormal-paper group-hover:border-abnormal-acid group-hover:text-abnormal-acid transition-colors">
                        <Activity size={22} />
                    </div>
                    <div className="flex-1">
                        <p className="label-mono text-abnormal-paperDim/60">{t("trails.heatmap")}</p>
                        <p className="font-display text-2xl uppercase leading-none mt-1 text-abnormal-paper">{t("trails.strava")}</p>
                    </div>
                    <ExternalLink className="text-abnormal-paperDim/60 group-hover:text-abnormal-acid" size={18} />
                </a>
            </div>

            {/* ---- FILTERS ---- */}
            <div data-testid="trails-filters" className="mt-12 border-y border-white/10 py-6 space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="label-mono text-abnormal-paperDim/60 flex items-center gap-2 mr-2 w-full sm:w-auto">
                        <Filter size={13} /> {t("trails.statusFilter")}
                    </span>
                    {STATES.map((s) => (
                        <button key={s.value} data-testid={`filter-state-${s.value}`} onClick={() => setState(s.value)}
                            className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${state === s.value ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/20 text-abnormal-paperDim hover:border-abnormal-paper hover:text-abnormal-paper"}`}>
                            {s.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="label-mono text-abnormal-paperDim/60 mr-2 w-full sm:w-auto">{t("trails.difficulty")}</span>
                    {DIFFICULTIES.map((d) => (
                        <button key={d.value} data-testid={`filter-difficulty-${d.value}`} onClick={() => setDifficulty(d.value)}
                            className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${difficulty === d.value ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/20 text-abnormal-paperDim hover:border-abnormal-paper hover:text-abnormal-paper"}`}>
                            {d.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="label-mono text-abnormal-paperDim/60 mr-2 w-full sm:w-auto">{t("trails.terrain")}</span>
                    {TERRAINS.map((tr) => (
                        <button key={tr.value} data-testid={`filter-terrain-${tr.value}`} onClick={() => setTerrain(tr.value)}
                            className={`px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${terrain === tr.value ? "bg-abnormal-acid text-black border-abnormal-acid" : "border-white/20 text-abnormal-paperDim hover:border-abnormal-paper hover:text-abnormal-paper"}`}>
                            {tr.label}
                        </button>
                    ))}
                    {hasFilters && (
                        <button data-testid="filter-reset" onClick={resetFilters}
                            className="ml-auto label-mono text-abnormal-paperDim hover:text-abnormal-acid inline-flex items-center gap-1 py-2">
                            <X size={12} /> {t("trails.resetFilters")}
                        </button>
                    )}
                </div>
            </div>

            {/* ---- TRAIL CARDS ---- */}
            <SectionMark n="01" title={lang === "en" ? "The trails" : "I trail"} className="mt-12"
                right={<span className="label-mono text-abnormal-paperDim/60">{filtered.length} / {trails.length}</span>} />
            {filtered.length === 0 ? (
                <div className="py-20 text-center text-abnormal-paperDim label-mono">{t("trails.empty")}</div>
            ) : (
                <div data-testid="trails-list" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {filtered.map((tr, idx) => {
                        const trName = (lang === "en" && tr.name_en) ? tr.name_en : tr.name;
                        const trSummary = (lang === "en" && tr.summary_en) ? tr.summary_en : tr.summary;
                        const firstQuote = Array.isArray(tr.rider_quotes) && tr.rider_quotes[0]?.text;
                        const status = trailStatus(tr, lang);
                        const trState = status.key;
                        const note = status.note;
                        const when = fmtDateTime(status.updatedAt, lang);
                        const cardNumber = String(idx + 1).padStart(2, "0");
                        const cardTotal  = String(filtered.length).padStart(2, "0");
                        return (
                            <article key={tr.id} data-testid={`trail-card-${tr.slug}`}
                                className="group bg-abnormal-charcoal border border-white/10 hover:border-abnormal-acid transition-colors duration-200 overflow-hidden flex flex-col">
                                <Link to={`/trail/${tr.slug}`} className="relative block overflow-hidden aspect-[16/10]">
                                    <img src={absolutizeUrl(tr.image_url)} alt={trName} loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                                    <span className="absolute top-3 left-3 label-mono text-abnormal-paper bg-black/70 border border-white/15 px-2 py-1">
                                        <span className="text-abnormal-acid">N° {cardNumber}</span> / {cardTotal}
                                    </span>
                                    {tr.is_trail_of_month && (
                                        <span className="absolute top-12 left-3 bg-abnormal-acid text-black label-mono px-2 py-1">
                                            ★ {t("trails.badgeMonth")}
                                        </span>
                                    )}
                                    <span className={`absolute top-3 right-3 bg-black/70 border ${diffColor(tr.difficulty)} label-mono px-2 py-1 inline-flex items-center gap-1.5`}>
                                        {DIFF_DOT[tr.difficulty] && <span className={`w-2 h-2 ${tr.difficulty === "black" ? "rotate-45" : "rounded-full"} ${DIFF_DOT[tr.difficulty]}`} />}
                                        {tr_(t, `trailMeta.difficulty.${tr.difficulty}`, tr.difficulty)}
                                    </span>
                                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
                                        <h3 className="font-display text-3xl md:text-4xl uppercase leading-[0.9] tracking-tight text-abnormal-paper">
                                            {trName}<span className="text-abnormal-acid">.</span>
                                        </h3>
                                        <div className="flex flex-col items-end gap-1.5">
                                            <StageBadge slug={tr.slug} />
                                            <StateBadge state={trState} label={status.label} />
                                        </div>
                                    </div>
                                </Link>

                                <div className="p-4 md:p-5 flex-1 flex flex-col">
                                    {/* Field-map strip: map fragment + tech data */}
                                    <div className="grid grid-cols-[88px_1fr] gap-3 items-stretch">
                                        <div className="border border-white/10 bg-black/40 overflow-hidden">
                                            <MapFragment seed={tr.slug} className="w-full h-full" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="border border-white/10 px-2 py-2">
                                                <p className="label-mono text-abnormal-paperDim/50 !text-[9px]">{t("trails.metaLength")}</p>
                                                <p className="font-display text-lg uppercase leading-none mt-1 text-abnormal-paper">{tr.length_km}<span className="text-abnormal-acid text-xs ml-0.5">km</span></p>
                                            </div>
                                            <div className="border border-white/10 px-2 py-2">
                                                <p className="label-mono text-abnormal-paperDim/50 !text-[9px]">{t("trails.metaElev")}</p>
                                                <p className="font-display text-lg uppercase leading-none mt-1 text-abnormal-paper">{tr.elevation_m}<span className="text-abnormal-acid text-xs ml-0.5">m</span></p>
                                            </div>
                                            <div className="col-span-2 border border-white/10 h-7 overflow-hidden relative">
                                                <ElevationProfile seed={tr.slug} className="w-full h-full" />
                                                <span className="absolute right-1.5 top-1 label-mono !text-[8px] text-abnormal-paperDim/50 hidden lg:inline">{ARGENTARIO_COORDS}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* State note (optional) */}
                                    {note && (
                                        <p className={`mt-3 text-xs leading-snug border-l-2 pl-2 ${
                                            trState === "warning" || trState.startsWith("wet") ? "border-abnormal-warn text-abnormal-paperDim"
                                            : trState === "closed" ? "border-abnormal-red text-abnormal-paperDim"
                                            : "border-abnormal-acid/60 text-abnormal-paperDim"
                                        }`}>{note}</p>
                                    )}

                                    {firstQuote ? (
                                        <p data-testid={`trail-card-quote-${tr.slug}`} className="text-abnormal-paperDim text-sm mt-3 flex-1 line-clamp-2 leading-snug italic">
                                            <span className="text-abnormal-acid not-italic">“</span>{firstQuote}<span className="text-abnormal-acid not-italic">”</span>
                                        </p>
                                    ) : (
                                        <p className="text-abnormal-paperDim text-sm mt-3 flex-1 line-clamp-2 leading-snug">{trSummary}</p>
                                    )}

                                    {when && (
                                        <p className="mt-3 label-mono text-abnormal-paperDim/50" data-testid={`trail-card-updated-${tr.slug}`}>
                                            {t("trails.lastUpdated")}: <span className="text-abnormal-paperDim">{when}</span>{status.source && <> · {status.source}</>}
                                        </p>
                                    )}

                                    {/* CTA row: Open trail + Komoot + Maps — large touch targets */}
                                    <div className="mt-4 flex flex-wrap gap-2 items-center">
                                        <Link to={`/trail/${tr.slug}`} data-testid={`trail-open-${tr.slug}`}
                                            className="btn-primary !px-4 !py-2.5 !text-[11px]">
                                            {t("trails.cardCta")} <ArrowRight size={12} />
                                        </Link>
                                        {tr.komoot_url && (
                                            <a href={tr.komoot_url} target="_blank" rel="noreferrer" data-testid={`trail-komoot-${tr.slug}`}
                                                className="btn-ghost !px-3 !py-2.5 !text-[11px]">
                                                <Mountain size={11} /> Komoot
                                            </a>
                                        )}
                                        {tr.google_maps_url && (
                                            <a href={tr.google_maps_url} target="_blank" rel="noreferrer" data-testid={`trail-maps-${tr.slug}`}
                                                className="btn-ghost !px-3 !py-2.5 !text-[11px]">
                                                <MapPin size={11} /> Maps
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* ---- COMBO LINES (moved below trails, collapsible) ---- */}
            {combos.length > 0 && (
                <section data-testid="combo-section" className="mt-20 border border-white/15 bg-abnormal-charcoal">
                    <button onClick={() => setShowCombos((v) => !v)}
                        data-testid="combo-toggle"
                        className="w-full text-left p-6 md:p-8 flex items-center justify-between gap-4 hover:bg-white/[0.02] transition-colors">
                        <div>
                            <p className="label-mono text-abnormal-acid flex items-center gap-2">
                                <Zap size={13} /> {t("combos.eyebrow")}
                            </p>
                            <h2 className="font-display text-3xl md:text-5xl uppercase leading-[0.95] mt-2 text-abnormal-paper">
                                {t("combos.title")}
                            </h2>
                        </div>
                        <span className="btn-ghost !py-2 !px-3">
                            {showCombos ? t("trails.hide") : t("trails.show")} ({combos.length})
                        </span>
                    </button>

                    {showCombos && (
                        <div data-testid="combos-list" className="px-6 md:px-8 pb-8 grid lg:grid-cols-2 gap-5">
                            {combos.map((c) => {
                                const name = lang === "en" && c.name_en ? c.name_en : c.name;
                                const summary = lang === "en" && c.summary_en ? c.summary_en : c.summary;
                                const exitNotes = lang === "en" && c.exit_notes_en ? c.exit_notes_en : c.exit_notes;
                                return (
                                    <article key={c.slug} data-testid={`combo-card-${c.slug}`}
                                        className="bg-black/50 border border-white/10 hover:border-abnormal-acid transition-colors p-5 md:p-6 flex flex-col gap-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <p className="label-mono text-abnormal-acid">
                                                {t("combos.sequenceTitle")}
                                            </p>
                                            <span className="label-mono bg-abnormal-acid text-black px-2 py-1">COMBO</span>
                                        </div>
                                        <h3 className="font-display text-2xl md:text-3xl uppercase leading-tight text-abnormal-paper">{name}</h3>
                                        <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-wider">
                                            {c.trail_slugs.map((slug, idx) => (
                                                <React.Fragment key={slug}>
                                                    <Link to={`/trail/${slug}`} data-testid={`combo-step-${c.slug}-${slug}`}
                                                        className="border border-white/20 px-2 py-1 hover:border-abnormal-acid hover:text-abnormal-acid transition-colors">
                                                        {slug}
                                                    </Link>
                                                    {idx < c.trail_slugs.length - 1 && <span className="text-abnormal-acid font-bold">→</span>}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                        <p className="text-sm text-abnormal-paperDim leading-relaxed">{summary}</p>
                                        <div className="grid grid-cols-3 gap-2 mt-2">
                                            <div className="border border-white/10 p-3">
                                                <p className="label-mono !text-[9px] text-abnormal-paperDim/50">{t("combos.kmLabel")}</p>
                                                <p className="font-display text-xl uppercase mt-1">{c.total_km} km</p>
                                            </div>
                                            <div className="border border-white/10 p-3">
                                                <p className="label-mono !text-[9px] text-abnormal-paperDim/50">{t("combos.elevLabel")}</p>
                                                <p className="font-display text-xl uppercase mt-1">{c.total_elevation_m} m</p>
                                            </div>
                                            <div className="border border-white/10 p-3">
                                                <p className="label-mono !text-[9px] text-abnormal-paperDim/50">{t("combos.trailsLabel")}</p>
                                                <p className="font-display text-xl uppercase mt-1">{c.trail_slugs.length}</p>
                                            </div>
                                        </div>
                                        {exitNotes && (
                                            <div className="border-l-2 border-abnormal-warn pl-3 py-1">
                                                <p className="label-mono text-abnormal-warn flex items-center gap-1">
                                                    <AlertTriangle size={11} /> {t("combos.exitTitle")}
                                                </p>
                                                <p className="text-sm text-abnormal-paperDim mt-1 leading-relaxed">{exitNotes}</p>
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </section>
            )}
            </div>
        </div>
    );
}
